/* eslint-disable react/destructuring-assignment */
/**
 * 組織ツリー関連のコンポーネントをまとめたファイルです。
 */
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import { Control, Controller, FieldValues, Path, UnPackAsyncDefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select, {
  components,
  MenuListProps,
  MenuProps,
  MultiValueProps,
  MultiValueRemoveProps,
  OptionProps,
} from 'react-select';
import { TreeCompany } from '../../api/model';

/**
 * 選択可能な要素の種類
 */
export type OptionType = 'company' | 'group' | 'team' | 'user';

export interface Option {
  type: OptionType; // 選択肢の種類
  value: string;
  label: string;
  ancestors: string[]; // 選択肢の先祖要素の ID
  duplicated: boolean;
}

/**
 * 組織ツリーから選択肢のリストを取得
 * @param treeCompanies 組織ツリー
 * @param isJapanese 日本語かどうか
 * @returns
 */
export const getOptions = (treeCompanies: TreeCompany[], isJapanese: boolean) => {
  const options: Option[] = [];
  treeCompanies.forEach((x) => {
    if (
      x.company_id == null ||
      x.groups == null ||
      x.company_name_ja == null ||
      x.company_name_en == null ||
      x.hidden === 1
    ) {
      return;
    }
    const companyId = `company_${x.company_id}`;
    const company: Option = {
      type: 'company',
      value: companyId,
      label:
        (isJapanese ? x.company_name_ja : x.company_name_en) +
        options
          .filter((q) => q.value === companyId)
          .map(() => ' ')
          .join(''),
      ancestors: [],
      duplicated: options.find((q) => q.value === companyId) != null,
    };
    options.push(company);
    x.groups.forEach((y) => {
      if (y.group_id == null || y.teams == null || y.group_name_ja == null || y.group_name_en == null) {
        return;
      }
      const groupId = `group_${y.group_id}`;
      const group: Option = {
        type: 'group',
        value: groupId,
        label:
          (isJapanese ? y.group_name_ja : y.group_name_en) +
          options
            .filter((q) => q.value === groupId)
            .map(() => ' ')
            .join(''),
        ancestors: [companyId],
        duplicated: options.find((q) => q.value === groupId) != null,
      };
      options.push(group);
      y.teams.forEach((z) => {
        if (
          z.team_id == null ||
          z.hidden === 1 ||
          z.users == null ||
          z.team_name_ja == null ||
          z.team_name_en == null
        ) {
          return;
        }
        const teamId = `team_${z.team_id}`;
        const team: Option = {
          type: 'team',
          value: teamId,
          label:
            (isJapanese ? z.team_name_ja : z.team_name_en) +
            options
              .filter((q) => q.value === teamId)
              .map(() => ' ')
              .join(''),
          ancestors: [companyId, groupId],
          duplicated: options.find((q) => q.value === teamId) != null,
        };
        options.push(team);
        z.users.forEach((w) => {
          if (w.user_id == null || w.user_name_ja == null || w.user_name_en == null) {
            return;
          }
          const userId = `user_${w.user_id}`;
          const user: Option = {
            type: 'user',
            value: userId,
            label:
              (isJapanese ? w.user_name_ja : w.user_name_en) +
              options
                .filter((q) => q.value === userId)
                .map(() => ' ')
                .join(''), // label と value が同じものがあると、key 重複エラーが発生してしまうので、空白をつけて対応
            ancestors: [companyId, groupId, teamId],
            duplicated: options.find((q) => q.value === userId) != null,
          };
          options.push(user);
        });
      });
    });
  });
  return options;
};

// 組織選択モーダルでは組織の開閉状態を他の要素に通知するために、context を利用しています。
const openIdsContext = createContext<string[]>([]); // 開いた状態の要素の一覧の context
const setOpenIdsContext = createContext<Dispatch<SetStateAction<string[]>>>(() => undefined); // 開閉状態を変更するための context

// どの要素にチェックボックスを付けるのか、Option に通知するために利用するためにも context を利用しています。
// react-select では追加のパラメータを子要素に共有するのが難しいためです。
const selectableTypesContext = createContext<OptionType[]>([]);

function OpenIdsProvider({ children }: { children: ReactNode }) {
  const [openIds, setOpenIds] = useState<string[]>([]);

  return (
    <openIdsContext.Provider value={openIds}>
      <setOpenIdsContext.Provider value={setOpenIds}>{children}</setOpenIdsContext.Provider>
    </openIdsContext.Provider>
  );
}

const useOpenIdsValue = () => useContext(openIdsContext);
const useOpenIdsSetValue = () => useContext(setOpenIdsContext);
const useSelectableTypesValue = () => useContext(selectableTypesContext);

/**
 * カスタムした選択肢
 * 参考：https://react-select.com/components
 * @param props
 * @returns
 */
function MyOption(props: OptionProps<Option>) {
  const openIds = useOpenIdsValue();
  const open = openIds.includes(props.data.value);
  const show = props.data.ancestors.every((x) => openIds.includes(x));
  const setOpenIds = useOpenIdsSetValue();
  const toggle = () =>
    setOpenIds((pre) => {
      if (open) {
        return pre.filter((x) => x !== props.data.value);
      }
      return [...pre, props.data.value];
    });
  const selectableTypes = useSelectableTypesValue();

  return (
    <div
      className={`organization-tree-item organization-tree-${props.data.type} ${show ? '' : 'hidden-item'}`}
      onClick={props.data.type === 'user' ? undefined : toggle}
    >
      {/* 選択肢種別がユーザー以外の場合は開閉状態を表示 */}
      {props.data.type !== 'user' && <i className={`fa-solid fa-caret-${open ? 'down' : 'right'}`} />}
      {/* チェックボックス表示対象かに応じて、チェックボックスを表示 */}
      {selectableTypes.includes(props.data.type) ? (
        <>
          <input
            type="checkbox"
            id={props.data.value}
            className="norm"
            checked={props.isSelected}
            onChange={() => {
              props.selectOption(props.data);
            }}
          />{' '}
          <label htmlFor={props.data.value} className="organization-tree">
            {props.label}
          </label>
        </>
      ) : (
        props.data.label
      )}
    </div>
  );
}

/**
 * カスタムしたメニュー
 * @param props
 * @returns
 */
function MyMenu(props: MenuProps<Option>) {
  return (
    <div className="modal-container active">
      <div className="modal-body">
        <div className="modal-content">
          <components.Menu {...props} className="popupfr popupfr340 nopad relative-position">
            <div
              className="modal-close"
              onClick={() => {
                if (document.activeElement != null) {
                  // ×ボタンが押されたときは、input からフォーカスを外すことで、メニューを閉じる
                  // TODO: ref を使った実装に変更
                  (document.activeElement as HTMLInputElement).blur();
                }
              }}
            >
              ×
            </div>
            {props.children}
          </components.Menu>
        </div>
      </div>
    </div>
  );
}

/**
 * カスタムしたメニューリスト
 * @param props
 * @returns
 */
function MyMenuList(props: MenuListProps<Option>) {
  return (
    <div className="dialogfr organization-tree-dialog">
      <components.MenuList {...props}>{props.children}</components.MenuList>
    </div>
  );
}

/**
 * カスタムした削除ボタン
 * @param props
 * @returns
 */
function MyMultiValueRemove(props: MultiValueRemoveProps<Option>) {
  return (
    // Focus が親要素に伝わって、メニューが開くことを避ける
    <div onFocus={(e) => e.stopPropagation()}>
      <components.MultiValueRemove {...props} />
    </div>
  );
}

/**
 * 選択済みの選択肢表示
 * @param props
 * @returns
 */
function MyMultiValue(props: MultiValueProps<Option, true>) {
  if (props.data.duplicated) {
    return null;
  }

  return <components.MultiValue {...props}>{props.children}</components.MultiValue>;
}

interface Props<T extends FieldValues> {
  id?: string | undefined;
  name: Path<UnPackAsyncDefaultValues<T>>;
  control: Control<T>;
  options: Option[];
  selectableTypes: OptionType[];
  isDisabled: boolean;
}

/**
 * 組織ツリーのメイン部分
 * @param
 * @returns
 */
function OrganizationTreeMultiSelect<T extends FieldValues>({
  id,
  name,
  control,
  options,
  selectableTypes,
  isDisabled,
}: Props<T>) {
  const { t } = useTranslation();

  return (
    <span className="multi-select">
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <OpenIdsProvider>
            <selectableTypesContext.Provider value={selectableTypes}>
              <Select
                id={id}
                placeholder={t('選択してください')}
                // カスタムコンポーネントを設定
                components={{
                  Option: MyOption,
                  Menu: MyMenu,
                  MenuList: MyMenuList,
                  MultiValueRemove: MyMultiValueRemove,
                  MultiValue: MyMultiValue,
                }}
                options={options}
                value={options.filter(
                  (x) =>
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    field.value != null && field.value.includes(x.value)
                )}
                onChange={(newValue) => {
                  field.onChange(newValue.map((x) => x.value));
                }}
                isMulti
                isDisabled={isDisabled}
                // 一応設定。詳細は MultiSelect を参照
                menuPortalTarget={document.querySelector('body')}
                isSearchable={false}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                // 編集不可のときにメニューが開かないようにする
                menuIsOpen={isDisabled ? false : undefined}
                // モックに合わせて、カスタムスタイルを当てる
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    width: '100%',
                    padding: '0',
                    borderRadius: '50px',
                    border: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: '#f4f4f4',
                    fontSize: '12px',
                    margin: '0',
                    minHeight: '25px',
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    paddingLeft: '8px',
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    padding: '0px 0px 0px 8px',
                  }),
                  dropdownIndicator: (baseStyles) => ({
                    ...baseStyles,
                    padding: '0px 8px 0px 0px',
                  }),
                  clearIndicator: (baseStyles) => ({
                    ...baseStyles,
                    padding: '0px 8px',
                  }),
                  menuList: (baseStyles) => ({
                    ...baseStyles,
                    padding: '0px',
                  }),
                  menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
                }}
              />
            </selectableTypesContext.Provider>
          </OpenIdsProvider>
        )}
      />
    </span>
  );
}

OrganizationTreeMultiSelect.defaultProps = {
  id: undefined,
};

export default OrganizationTreeMultiSelect;
